import { useMutation } from '@tanstack/react-query'
import { readFirstFactor } from '../../lib/twoFactor/firstFactor'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { toast } from 'sonner'
import { authApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { broadcastAuthChange, getAuthSyncChannel } from '../../setup/auth/authSync'
import type {
  LoginCredentials,
  TokenResponse,
  SignUpRequest,
  SignUpResponse,
  ForgotPasswordRequest,
  VerifyEmailRequest,
  SetNewPasswordRequest,
  GoogleOAuthRequest,
  LogoutRequest,
} from '../../services/api/auth.api'

// Re-export types for backward compatibility
export type {
  LoginCredentials,
  TokenResponse,
  SignUpRequest,
  SignUpResponse,
  ForgotPasswordRequest,
  VerifyEmailRequest,
  SetNewPasswordRequest,
  GoogleOAuthRequest,
  LogoutRequest,
}

// ============================================
// AUTHENTICATION MUTATIONS
// ============================================

/**
 * A session now exists (cookies are set): refresh the identity queries and tell
 * the other tabs of this origin, so they drop their stale identity/org state and
 * re-bootstrap. Called from every path that ends in a session — password login,
 * social login, and completing a two-factor challenge.
 */
export function announceSession() {
  // Trigger permissions fetch first (critical for auth and feature flags)
  queryClient.invalidateQueries({ queryKey: queryKeys.user.permissions })
  // Trigger user profile fetch which will handle Redux state
  queryClient.invalidateQueries({ queryKey: queryKeys.user.profile })
  const channel = getAuthSyncChannel()
  if (channel) broadcastAuthChange(channel, 'login')
}

/**
 * Hook for user login
 * Stores tokens in localStorage and triggers permissions and user profile fetch
 */
export function useLogin() {
  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: (data: unknown) => {
      // A `200` can be a second-factor challenge, which carries NO cookies —
      // there is no session to announce until the challenge is completed.
      if (readFirstFactor(data).kind === 'session') announceSession()
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for user sign up
 */
export function useSignUp() {
  return useMutation({
    mutationFn: (data: SignUpRequest) => authApi.signUp(data),
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for forgot password (request reset email)
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) => authApi.forgotPassword(data),
    onSuccess: () => {
      toast.success('Password reset email sent. Please check your inbox.')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for verifying email address
 */
export function useVerifyEmail() {
  return useMutation({
    mutationFn: (data: VerifyEmailRequest) => authApi.verifyEmail(data),
    onSuccess: () => {
      toast.success('Email verified successfully!')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for setting new password after forgot password
 */
export function useSetNewPassword() {
  return useMutation({
    mutationFn: (data: SetNewPasswordRequest) => authApi.setNewPassword(data),
    onSuccess: () => {
      toast.success('Password updated successfully!')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for Google OAuth authentication
 */
export function useGoogleOAuth() {
  return useMutation({
    mutationFn: (data: GoogleOAuthRequest) => authApi.googleOAuth(data),
    onSuccess: (data: unknown) => {
      if (readFirstFactor(data).kind === 'session') announceSession()
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for completing Google OAuth registration
 */
export function useGoogleOAuthRegister() {
  return useMutation({
    mutationFn: ({ params, payload }: { params: string; payload: { state: string; code: string } }) =>
      authApi.googleOAuthRegister(params, payload),
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for user logout
 * Clears tokens, FCM token, and resets Zustand auth store
 */
export function useLogout() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      try {
        await authApi.logout()
      } catch (error) {
        // Ignore logout API errors, still proceed with local cleanup
      }
    },
    onSuccess: () => {
      // Clear local storage
      localStorage.removeItem('fcmToken')
      localStorage.removeItem('fcmTokenExpiry')

      // Cancel all ongoing queries to prevent further requests
      queryClient.cancelQueries()

      // Clear React Query cache
      queryClient.clear()

      // Reset Zustand auth store
      useAuthStore.getState().Logout_(false)

      // Tell other tabs the shared session cookie was cleared so they can drop
      // their now-stale authenticated state.
      const channel = getAuthSyncChannel()
      if (channel) broadcastAuthChange(channel, 'logout')
    },
  })
}
